import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { memo } from 'preact/compat';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { dashboardApi } from '@/api/dashboard';
import { useTheme } from '@/hooks/useTheme';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ResponsivePie } from '@nivo/pie';
import { PieChart } from 'lucide-preact';
const palette = [
    '#2563eb', '#059669', '#f59e0b', '#ef4444', '#7c3aed', '#06b6d4',
    '#84cc16', '#f97316', '#db2777', '#0ea5e9', '#10b981', '#3b82f6',
    '#8b5cf6', '#eab308', '#22c55e', '#14b8a6', '#e11d48', '#a855f7',
    '#f43f5e', '#38bdf8'
];
export const LeadsBySourceTile = memo(({ workspaceId, range, className = '', mock = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { resolvedTheme } = useTheme();
    const isMobile = useIsMobile();
    useEffect(() => {
        if (!workspaceId)
            return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            const resp = await dashboardApi.getLeadsBySource(workspaceId, range, mock);
            if (!cancelled) {
                if (resp.error)
                    setError(resp.error);
                else
                    setData(resp.data || null);
                setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [workspaceId, range, mock]);
    const nivoTheme = useMemo(() => {
        const isDark = resolvedTheme === 'dark';
        const axisText = isDark ? '#cbd5e1' : '#334155';
        const gridLine = isDark ? '#334155' : '#e2e8f0';
        const legendText = isDark ? '#e2e8f0' : '#334155';
        return {
            background: 'transparent',
            text: { fill: axisText },
            axis: { domain: { line: { stroke: gridLine } }, ticks: { line: { stroke: gridLine }, text: { fill: axisText } }, legend: { text: { fill: legendText } } },
            grid: { line: { stroke: gridLine, strokeDasharray: '4 4' } },
            legends: { text: { fill: legendText } },
            tooltip: { container: { background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#e2e8f0' : '#0f172a' } }
        };
    }, [resolvedTheme]);
    const pieData = useMemo(() => {
        if (!data?.Sources)
            return [];
        return data.Sources.map(s => ({ id: s.Source, label: s.Source, value: s.Count }));
    }, [data]);
    return (_jsxs("div", { className: `tile flex flex-col ${className}`.trim(), children: [_jsxs("div", { className: "flex items-start justify-between mb-4", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight", children: "Leads by Source" }), _jsx(PieChart, { className: "w-5 h-5 text-primary-600 dark:text-primary-400 opacity-70" })] }), loading && (_jsx("div", { className: "flex-1 flex items-center justify-center", style: { minHeight: 280 }, children: _jsx("div", { className: "h-48 w-48 rounded-full bg-shimmer-light animate-shimmer" }) })), !loading && error && _jsx("div", { className: "text-sm text-red-600 dark:text-red-400", children: error }), !loading && !error && (!data || pieData.length === 0) && (_jsx("div", { className: "text-sm text-slate-500 dark:text-slate-400", children: "No source data yet" })), !loading && !error && data && pieData.length > 0 && (_jsx("div", { style: { height: 320 }, children: _jsx(ResponsivePie, { data: pieData, colors: palette, margin: isMobile ? { top: 20, right: 20, bottom: 20, left: 20 } : { top: 40, right: 120, bottom: 40, left: 120 }, innerRadius: 0.5, padAngle: 0.7, cornerRadius: 3, activeOuterRadiusOffset: 8, enableArcLinkLabels: !isMobile, arcLinkLabelsSkipAngle: 10, arcLinkLabelsTextColor: { from: 'color' }, arcLinkLabelsThickness: 2, arcLinkLabelsColor: { from: 'color' }, arcLinkLabelsDiagonalLength: 16, arcLinkLabelsStraightLength: 24, arcLinkLabelsTextOffset: 6, enableArcLabels: false, theme: nivoTheme }) }))] }));
});
